import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  Loader2,
  Save,
  LayoutList,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { clickupApi } from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';

const ClickUpLogo = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none">
    <path
      d="M3 14.5L7.5 9.5L12 14.5L16.5 9.5L21 14.5"
      stroke="url(#cu-grad-config)"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <defs>
      <linearGradient id="cu-grad-config" x1="3" y1="12" x2="21" y2="12" gradientUnits="userSpaceOnUse">
        <stop stopColor="#7B68EE" />
        <stop offset="1" stopColor="#FF4F9A" />
      </linearGradient>
    </defs>
  </svg>
);

export function SettingsClickupConfig() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  const agencyOrg = user?.organizations?.find((org: any) => org.type === 'AGENCY');
  const organizationId = (agencyOrg as any)?.org_id || agencyOrg?.id;

  const [defaultListIdInput, setDefaultListIdInput] = useState('');
  const [projectListIds, setProjectListIds] = useState<Record<string, string>>({});
  const [initialized, setInitialized] = useState(false);

  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ['clickup-config', organizationId],
    queryFn: () => clickupApi.getConfig(organizationId!),
    enabled: !!organizationId,
  });

  useEffect(() => {
    if (config && !initialized) {
      setDefaultListIdInput(config.defaultListId || '');
      setProjectListIds({ ...config.projectOverrides });
      setInitialized(true);
    }
  }, [config, initialized]);

  const saveDefaultMutation = useMutation({
    mutationFn: () =>
      clickupApi.saveConfig(organizationId!, defaultListIdInput.trim()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clickup-config', organizationId] });
    },
  });

  const saveProjectMutation = useMutation({
    mutationFn: ({ projectId, listId }: { projectId: string; listId: string | null }) =>
      clickupApi.saveProjectOverride(projectId, listId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clickup-config', organizationId] });
    },
  });

  const projects: Array<{ id: string; name: string }> = config?.projects ?? [];

  return (
    <div className="space-y-6">
      <div className="mb-2">
        <Link
          to="/settings/integrations"
          className="inline-flex items-center gap-1 text-sm text-grey-500 hover:text-secondary"
        >
          <ArrowLeft className="w-4 h-4" />
          Integrations
        </Link>
      </div>

      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
          <ClickUpLogo />
        </div>
        <div className="flex-1">
          <h2 className="text-heading-3 text-secondary">ClickUp</h2>
          <p className="text-sm text-grey-500 mt-1">
            Configure ClickUp ticket creation for Slack triage alerts
          </p>
        </div>
        {configLoading ? (
          <Loader2 className="w-4 h-4 animate-spin text-grey-400" />
        ) : config?.tokenConfigured ? (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-success/10 text-success text-xs font-medium rounded-full">
            <CheckCircle className="w-3 h-3" />
            Token configured
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-warning/10 text-warning text-xs font-medium rounded-full">
            <AlertCircle className="w-3 h-3" />
            Token not configured
          </span>
        )}
      </div>

      {!config?.tokenConfigured && !configLoading && (
        <Card className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
            <p className="text-sm text-grey-600">
              Add{' '}
              <code className="bg-grey-100 px-1 py-0.5 rounded">CLICKUP_TOKEN</code>{' '}
              to your environment variables to enable ClickUp ticket creation.
            </p>
          </div>
        </Card>
      )}

      {/* Default List ID */}
      <Card className="p-6">
        <h3 className="text-heading-3 text-secondary mb-1">Default List ID</h3>
        <p className="text-sm text-grey-500 mb-4">
          The ClickUp list where tickets will be created when no project override is set.
        </p>
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={defaultListIdInput}
            onChange={(e) => setDefaultListIdInput(e.target.value)}
            placeholder="Enter ClickUp list ID"
            className="flex-1 px-3 py-2 border border-grey-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent"
          />
          <Button
            onClick={() => saveDefaultMutation.mutate()}
            disabled={saveDefaultMutation.isPending}
          >
            {saveDefaultMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save
          </Button>
        </div>
        {saveDefaultMutation.isSuccess && (
          <p className="mt-2 text-sm text-success">Saved successfully.</p>
        )}
      </Card>

      {/* Project Overrides */}
      <Card className="p-6">
        <h3 className="text-heading-3 text-secondary mb-1">Project Overrides</h3>
        <p className="text-sm text-grey-500 mb-4">
          Override the default list ID for specific projects.
        </p>

        {configLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-grey-400 animate-spin" />
          </div>
        ) : projects.length === 0 ? (
          <p className="text-sm text-grey-500">No projects found.</p>
        ) : (
          <div className="space-y-3">
            {projects.map((project: any) => (
              <div
                key={project.id}
                className={`p-4 rounded-lg border transition-colors ${
                  projectListIds[project.id]
                    ? 'border-accent/30 bg-accent/5'
                    : 'border-grey-200 bg-grey-50'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                    projectListIds[project.id] ? 'bg-accent/10 text-accent' : 'bg-grey-100 text-grey-400'
                  }`}>
                    <LayoutList className="w-4 h-4" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-secondary text-sm truncate" title={project.name}>
                      {project.name}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <input
                      type="text"
                      value={projectListIds[project.id] || ''}
                      onChange={(e) =>
                        setProjectListIds((prev) => ({ ...prev, [project.id]: e.target.value }))
                      }
                      placeholder="List ID (leave empty for default)"
                      className="px-3 py-1.5 bg-white border border-grey-200 rounded-lg text-sm text-secondary focus:ring-2 focus:ring-accent/50 focus:border-accent min-w-[220px]"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        saveProjectMutation.mutate({
                          projectId: project.id,
                          listId: projectListIds[project.id]?.trim() || null,
                        })
                      }
                      disabled={saveProjectMutation.isPending}
                    >
                      <Save className="w-3 h-3" />
                      Save
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
